"use client";

import { Button } from "@/components/ui/button";
import { useAppDispatch } from "@/redux/hooks";
import { AlertTriangle, Plane, Search } from "lucide-react";
import { refetch } from "@/feature/flight/search/state/slices/flight-search-status.slice";

type NoFlightsFoundProps = {
  mode?: "empty" | "error";
};

export function NoFlightsFound({ mode = "empty" }: NoFlightsFoundProps) {
  const dispatch = useAppDispatch();
  const isError = mode === "error";
  return (
    <div className="flex flex-col items-center justify-center mt-5">
      <div className="flex flex-col items-center text-center max-w-md space-y-6">
        {/* Icon */}
        <div className="relative">
          <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center">
            {isError ? (
              <AlertTriangle className="w-12 h-12 text-destructive" />
            ) : (
              <Plane className="w-12 h-12 text-muted-foreground" />
            )}
          </div>
          <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center">
            <Search className="w-4 h-4 text-destructive" />
          </div>
        </div>

        {/* Main message */}
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-foreground">
            {isError ? "Something went wrong" : "No flights found"}
          </h2>

          <p className="text-muted-foreground">
            {isError
              ? "We couldn&lsquo;t load flight results right now."
              : "We couldn&lsquo;t find any flights matching your search."}
          </p>
        </div>

        {/* Suggestions */}
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>{isError ? "Try again:" : "Try adjusting your search:"}</p>
          <ul className="space-y-1 text-left">
            {isError ? (
              <>
                <li>{"• Check your internet connection"}</li>
                <li>{"• Retry the search"}</li>
              </>
            ) : (
              <>
                <li>{"• Check your departure and arrival cities"}</li>
                <li>{"• Try different dates"}</li>
                <li>{"• Consider nearby airports"}</li>
                <li>{"• Adjust your filters"}</li>
              </>
            )}
          </ul>
        </div>

        <Button
          onClick={() => {
            dispatch(refetch(1));
          }}
          className="mt-6"
        >
          <Search className="w-4 h-4 mr-2" />
          {isError ? "Retry Search" : "Search again"}
        </Button>
      </div>
    </div>
  );
}
